import { Button, CircularProgress, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'web-components-ts';
import { ResetPasswordNonAuthSchema } from '../../../attrs/formValidation';
import { StyledFooterBar, StyledFooterBarContainer } from '../../../elements';
import { isError, isLoading } from '../../../helpers/utils';
import { resetPasswordNonAuth } from '../../../redux/auth/actions';
import { getResetPassword } from '../../../redux/auth/selectors';
import FormAlert from '../FormAlert';

interface SetPasswordFormProps {
  token: string;
}

const SetPasswordForm = ({ token }: SetPasswordFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleResetPassword = (values: { password: string; confirm_password: string }) =>
    dispatch(resetPasswordNonAuth(values, token));
  const updateLoadingState = useSelector(getResetPassword);

  return (
    <Formik
      initialValues={{ password: '', confirm_password: '' }}
      validationSchema={ResetPasswordNonAuthSchema}
      onSubmit={(values, formikHelpers) => {
        handleResetPassword(values);
        formikHelpers.resetForm();
      }}
    >
      {({ values, dirty, handleChange, handleBlur, handleSubmit, isSubmitting, errors, touched }) => (
        <form>
          <TextField
            label={t('profile.new_password')}
            fullWidth
            id="password"
            margin="normal"
            name="password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            required
            type="password"
            error={(errors.password && touched.password) || isError(updateLoadingState.status)}
            helperText={errors.password && touched.password ? t(`form.validate.${errors.password}`) : ''}
          />

          <TextField
            label={t('profile.confirm_password')}
            fullWidth
            id="confirm_password"
            margin="normal"
            name="confirm_password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.confirm_password}
            required
            type="password"
            error={(errors.confirm_password && touched.confirm_password) || isError(updateLoadingState.status)}
            helperText={
              errors.confirm_password && touched.confirm_password ? t(`form.validate.${errors.confirm_password}`) : ''
            }
          />
          {isError(updateLoadingState.status) && <FormAlert message={t('errors.user.reset_password')} />}
          <StyledFooterBar position={isMobile ? 'sticky' : 'fixed'}>
            <Toolbar>
              <StyledFooterBarContainer maxWidth="md" theme={theme}>
                <div />
                <Button
                  disabled={isSubmitting || Object.keys(errors).length !== 0 || !dirty}
                  endIcon={
                    isLoading(updateLoadingState.status) && (
                      <CircularProgress color="inherit" size={14} style={{ marginLeft: '1rem' }} />
                    )
                  }
                  color="primary"
                  onClick={() => handleSubmit()}
                  variant="contained"
                  type="submit"
                >
                  {t('form.reset_password')}
                </Button>
              </StyledFooterBarContainer>
            </Toolbar>
          </StyledFooterBar>
        </form>
      )}
    </Formik>
  );
};

export default SetPasswordForm;
